import { useState, useEffect, useCallback } from "react";
import { AdminLayout } from "@/project/jain_elibrary-admin/components/layout/AdminLayout";
import { StatsCard } from "@/project/jain_elibrary-admin/components/dashboard/StatsCard";
import { Button } from "@/components/ui/button";
import { adminService, Statistics } from "../services/adminService";
import { AdminPageTitle } from "@/project/jain_elibrary-admin/components/shared/AdminPageTitle";
import { RefreshCw } from "lucide-react";

const formatNumber = (value: number | null | undefined): string =>
  value !== null && value !== undefined ? value.toLocaleString() : "—";

const Dashboard = () => {
  const [statistics, setStatistics] = useState<Statistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStatistics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminService.getStatistics();
      setStatistics(data);
    } catch {
      setStatistics(null);
      setError(
        "We couldn't load statistics. Check your connection and try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatistics();
  }, [loadStatistics]);

  return (
    <AdminLayout>
      <div className="space-y-4 md:space-y-6">
        <AdminPageTitle
          title="Dashboard"
          description="Overview of library statistics and activity."
          actions={
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={loading}
              onClick={() => void loadStatistics()}
              className="gap-2"
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
                aria-hidden
              />
              Refresh
            </Button>
          }
        />

        {error ? (
          <div
            className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
            role="alert"
          >
            <p className="font-medium">{error}</p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-3"
              onClick={() => void loadStatistics()}
            >
              Try again
            </Button>
          </div>
        ) : null}

        {/* Core Statistics */}
        <div>
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            Core Statistics
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatsCard
              title="Total Books"
              value={formatNumber(statistics?.total_books)}
              loading={loading}
              className="border-l-4 border-l-blue-500"
            />
            <StatsCard
              title="Total Users"
              value={formatNumber(statistics?.total_users)}
              loading={loading}
              className="border-l-4 border-l-green-500"
            />
            <StatsCard
              title="Total Downloads"
              value={formatNumber(statistics?.total_downloads)}
              loading={loading}
              className="border-l-4 border-l-purple-500"
            />
            <StatsCard
              title="Total Donations"
              value={formatNumber(statistics?.total_donations)}
              loading={loading}
              className="border-l-4 border-l-red-500"
            />
          </div>
        </div>

        {/* Content Statistics */}
        <div>
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            Content Statistics
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatsCard
              title="Articles"
              value={formatNumber(statistics?.total_articles)}
              loading={loading}
            />
            <StatsCard
              title="Audio Files"
              value={formatNumber(statistics?.total_audio)}
              loading={loading}
            />
            <StatsCard
              title="Manuscripts"
              value={formatNumber(statistics?.total_manuscripts)}
              loading={loading}
            />
            <StatsCard
              title="Agam"
              value={formatNumber(statistics?.total_agam)}
              loading={loading}
            />
          </div>
        </div>

        {/* Library Statistics */}
        <div>
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            Library Statistics
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            <StatsCard
              title="Authors"
              value={formatNumber(statistics?.total_authors)}
              loading={loading}
            />
            <StatsCard
              title="Publishers"
              value={formatNumber(statistics?.total_publishers)}
              loading={loading}
            />
            <StatsCard
              title="Catalogues"
              value={formatNumber(statistics?.total_catalogues)}
              loading={loading}
            />
            <StatsCard
              title="Magazines"
              value={formatNumber(statistics?.total_magazines)}
              loading={loading}
            />
          </div>
        </div>

        {/* Requests & Feedback */}
        <div>
          <h3 className="mb-4 text-lg font-semibold text-foreground">
            Requests & Feedback
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <StatsCard
              title="Upload Requests"
              value={formatNumber(statistics?.total_upload_requests)}
              loading={loading}
            />
            <StatsCard
              title="Book Requests"
              value={formatNumber(statistics?.total_book_requests)}
              loading={loading}
            />
            <StatsCard
              title="Feedback"
              value={formatNumber(statistics?.total_feedback)}
              loading={loading}
            />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default Dashboard;
